// HELPER
import {
  FLOW_GATEWAY,
  cleanSortParam,
  initHttpParams,
  setWorkObjectTypeIdPath,
  WHHttpParams,
  IWHEntryPreviewDTO,
} from '@workheld/workheld-shared-lib';

// ENVIRONMENT
import { environment } from '../../environments/environment';

// ANGULAR
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

// RxJS 6
import { forkJoin, Observable, of } from 'rxjs';

@Injectable()
export class EntryAsyncService {
  constructor(private httpClient: HttpClient) {}

  // ENTRY PREVIEW
  public initEntryPreviewHttpParamsFormGroup(): FormGroup {
    return new FormBuilder().group({
      page: [0],
      sort: ['clientCreationDate,DESC'],
      term: [null],
      sortMode: ['DESC'],
      filter: [null],
      entryTypeId: [null],
      entryTypeIds: [null],
      entryStatusId: [null],

      startDate: [null],
      endDate: [null],
      equipmentId: [null],
      customerId: [null],
      targetState: [null],
      createdBy: [null],
    });
  }

  // ENTRY PROCESS
  public initEntryProcessFormGroup(): FormGroup {
    return new FormBuilder().group({
      entryStatusId: [null],
      managerComment: [null],
      feedback: [null, Validators.required],
    });
  }

  public getEntryPreview(
    params: WHHttpParams,
    targetParent: string,
    targetID: string,
    workObjectTypeId: number,
  ): Observable<IWHEntryPreviewDTO> {
    const httpParams: HttpParams = initHttpParams(params);
    switch (targetParent) {
      case 'ALL':
        return this.getEntryPreviewForAllTypes(httpParams);
      case 'EQUIPMENT':
        return this.getEntryPreviewByForEquipment(targetID, httpParams);
      case 'WORK_OBJECT':
        return this.getEntryPreviewByForWorkObject(
          targetID,
          workObjectTypeId,
          httpParams,
        );
      case 'WORK_ORDER':
        return this.getEntryPreviewByForWorkOrder(targetID, httpParams);
      case 'BOMNODE':
        return this.getEntryPreviewByForBomNode(targetID, httpParams);
      default:
        return of(null);
    }
  }

  private getEntryPreviewForAllTypes(
    httpParams: HttpParams,
  ): Observable<IWHEntryPreviewDTO> {
    httpParams = httpParams.set('sort', cleanSortParam(httpParams.get('sort')));
    if (!httpParams.get('filter')) {
      httpParams = httpParams.set('filter', 'ALL');
    }
    return this.httpClient.get<IWHEntryPreviewDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/entries/preview`,
      { params: httpParams },
    );
  }

  // WORK OBJECT LEVEL
  private getEntryPreviewByForWorkObject(
    workObjectId: string,
    workObjectTypeId: number,
    httpParams: HttpParams,
  ): Observable<IWHEntryPreviewDTO> {
    httpParams = httpParams.set('sort', cleanSortParam(httpParams.get('sort')));
    const workObjectTargetEndpoint: string =
      setWorkObjectTypeIdPath(workObjectTypeId);
    return this.httpClient.get<IWHEntryPreviewDTO>(
      `${
        environment.apiUrl + FLOW_GATEWAY
      }/${workObjectTargetEndpoint}/${workObjectId}/entries/preview`,
      { params: httpParams },
    );
  }

  // WORK ORDER LEVEL
  private getEntryPreviewByForWorkOrder(
    workOrderId: string,
    httpParams: HttpParams,
  ): Observable<IWHEntryPreviewDTO> {
    httpParams = httpParams.set('sort', cleanSortParam(httpParams.get('sort')));
    return this.httpClient.get<IWHEntryPreviewDTO>(
      `${
        environment.apiUrl + FLOW_GATEWAY
      }/workOrders/${workOrderId}/entries/preview`,
      { params: httpParams },
    );
  }

  // EQUIPMENT LEVEL
  private getEntryPreviewByForEquipment(
    equipmentId: string,
    httpParams: HttpParams,
  ): Observable<IWHEntryPreviewDTO> {
    // TODO: Equipment Entry Preview endpoint still has old sorting behavior in backend
    httpParams = httpParams.set('sort', cleanSortParam(httpParams.get('sort')));
    return this.httpClient.get<IWHEntryPreviewDTO>(
      `${
        environment.apiUrl + FLOW_GATEWAY
      }/equipments/${equipmentId}/entries/preview`,
      { params: httpParams },
    );
  }

  // BOMNODE LEVEL
  private getEntryPreviewByForBomNode(
    bomNodeId: string,
    httpParams: HttpParams,
  ): Observable<IWHEntryPreviewDTO> {
    return this.httpClient.get<IWHEntryPreviewDTO>(
      `${
        environment.apiUrl + FLOW_GATEWAY
      }/bomnode/${bomNodeId}/entries/preview`,
      { params: httpParams },
    );
  }

  public updateEntryByEntryID(
    entryID: string,
    entryPayload: any,
  ): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.apiUrl + FLOW_GATEWAY}/entries/${entryID}`,
      entryPayload,
    );
  }

  public updateDefectEntryByEntryID(
    entryID: string,
    entryPayload: any,
  ): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.apiUrl + FLOW_GATEWAY}/entries/defect/${entryID}`,
      entryPayload,
    );
  }

  public getDocumentationCount(): Observable<{
    entries: number;
    entryFiles: number;
    reports: number;
    checklists: number;
    eventLogs?: number;
  }> {
    const base = environment.apiUrl + FLOW_GATEWAY;
    const params = new HttpParams().set('filter', 'UNASSIGNED');
    return forkJoin({
      entries: this.httpClient.get<number>(`${base}/entries/count`, { params }),
      entryFiles: this.httpClient.get<number>(`${base}/entryfiles/count`, {
        params,
      }),
      reports: this.httpClient.get<number>(`${base}/reportfiles/count`, {
        params,
      }),
      checklists: this.httpClient.get<number>(
        `${base}/checklistInstances/count`,
        { params },
      )
    });
  }
}
